import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, Shield, ShieldCheck, Zap, BookOpen, ShieldOff, ChevronDown } from 'lucide-react';
import type { PermissionMode } from '@/lib/store/uiStore';
import { useUIStore } from '@/lib/store/uiStore';
import { cn } from '@/lib/utils';

interface ModeDef {
  value: PermissionMode;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const MODES: ModeDef[] = [
  {
    value: 'default',
    label: 'Ask permission',
    description: 'Agent asks before each tool use.',
    icon: Shield,
  },
  {
    value: 'acceptEdits',
    label: 'Accept edits',
    description: 'Auto-allow file edits only (still asks for web/shell).',
    icon: ShieldCheck,
  },
  {
    value: 'auto',
    label: 'Auto-allow safe',
    description: 'Auto-allow web, edits, reads — recommended.',
    icon: Zap,
  },
  {
    value: 'plan',
    label: 'Plan mode',
    description: 'Read-only — no edits, no shell.',
    icon: BookOpen,
  },
  {
    value: 'bypassPermissions',
    label: 'Bypass all',
    description: 'Auto-allow everything. Sandbox use only.',
    icon: ShieldOff,
  },
];

const DEF_BY_VALUE: Record<PermissionMode, ModeDef> =
  Object.fromEntries(MODES.map((m) => [m.value, m])) as Record<PermissionMode, ModeDef>;

export function PermissionMenu() {
  const mode = useUIStore((s) => s.permissionMode);
  const setMode = useUIStore((s) => s.setPermissionMode);
  const Icon = DEF_BY_VALUE[mode].icon;
  const accent =
    mode === 'bypassPermissions' ? 'text-warning'
    : mode === 'plan' ? 'text-info'
    : mode === 'auto' ? 'text-accent'
    : '';

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="focus-ring flex h-7 items-center gap-1 rounded-md px-1.5 text-[12px] text-fg-muted hover:bg-surface-2 hover:text-fg"
          title={`Permission: ${DEF_BY_VALUE[mode].label}`}
          aria-label="Permission mode"
        >
          <Icon className={cn('h-3.5 w-3.5', accent)} />
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[260px] rounded-lg border border-border bg-canvas p-1 shadow-menu animate-fade-in"
        >
          <DropdownMenu.Label className="px-2 py-1 text-2xs uppercase tracking-wide text-fg-subtle">
            Permission mode
          </DropdownMenu.Label>
          {MODES.map((m) => {
            const M = m.icon;
            const selected = m.value === mode;
            return (
              <DropdownMenu.Item
                key={m.value}
                className="menu-item items-start py-1.5"
                onSelect={() => setMode(m.value)}
              >
                <Check
                  className={cn(
                    'mt-0.5 h-3.5 w-3.5 shrink-0',
                    selected ? 'text-accent' : 'opacity-0'
                  )}
                />
                <M className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-muted" />
                <div className="flex-1">
                  <div className="text-[13px] font-medium text-fg">{m.label}</div>
                  <div className="text-[11.5px] leading-tight text-fg-muted">{m.description}</div>
                </div>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
